import { useEffect, useMemo, useRef, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Check, ChevronDown, RefreshCw, Search, SlidersHorizontal, X } from 'lucide-react'
import { useNavigate, useSearchParams } from 'react-router'
import { Pagination, StatePanel } from '@/components/common'
import { useOrganization } from '@/contexts/OrganizationContext'
import { useDebouncedValue } from '@/hooks/useDebouncedValue'
import { prepacksApi } from '@/services/api'
import { normalizePrepack, packRecipeId, packTypeLabel, resolveTemplates } from './prepackCardModel'
import { ALL_COLUMNS, DEFAULT_COLUMNS, ODDS_FILTERS, STATUS_FILTERS, nextSort, statusFilterLabel, statusParams } from './prepackLibraryModel'
import PackListOverlays from './PackListOverlays'
import PrepackListTable from './PrepackListTable'
import { schedulePath } from './prepackSchedulePaths'
import { useScheduleSources } from './prepackScheduleQueries'
import { useLastPageGuard, useListPage, usePackList } from './usePackList'
import { prepackSportLabel } from '@/utils/kambiSports'

const LIBRARY_PAGE_SIZE = 25

export default function PrepacksLibraryPage() {
  const navigate = useNavigate()
  const { currentOrg, hasProductAccess } = useOrganization()
  const slug = currentOrg?.slug
  const queryClient = useQueryClient()
  const canConfigure = hasProductAccess('prepacks', 'configure')
  const canPublish = hasProductAccess('prepacks', 'publish')

  const [search, setSearch] = useState('')
  const debouncedSearch = useDebouncedValue(search.trim())
  const [statusFilter, setStatusFilter] = useState('all')
  const [leagueFilter, setLeagueFilter] = useState('all')
  const [sportFilter, setSportFilter] = useState('all')
  const [typeFilter, setTypeFilter] = useState('all')
  const [oddsFilter, setOddsFilter] = useState('all')
  // A save's View opens the packs of one schedule or template (#1089).
  const [searchParams, setSearchParams] = useSearchParams()
  const scheduleFilter = searchParams.get('schedule') || ''
  const templateFilter = searchParams.get('template') || ''
  const clearSourceFilter = () => setSearchParams((current) => {
    const next = new URLSearchParams(current)
    next.delete('schedule')
    next.delete('template')
    return next
  }, { replace: true })
  const [visibleColumns, setVisibleColumns] = useState(DEFAULT_COLUMNS)
  const [sort, setSort] = useState({ key: 'ai', direction: 'desc' })
  const [columnsOpen, setColumnsOpen] = useState(false)
  // The packs region, where focus goes when a delete took away the control
  // that asked for it (#1054).
  const packsRegionRef = useRef(null)

  // A new organization, filter, settled search or sort starts on the first page.
  const { page, setPage } = useListPage(JSON.stringify([slug, statusFilter, sportFilter, leagueFilter, typeFilter, oddsFilter, debouncedSearch, scheduleFilter, templateFilter, sort.key, sort.direction]))

  const params = {
    ...statusParams(statusFilter),
    ...(sportFilter !== 'all' ? { sport: sportFilter } : {}),
    ...(leagueFilter !== 'all' ? { league: leagueFilter } : {}),
    ...(typeFilter !== 'all' ? { type: typeFilter } : {}),
    ...(oddsFilter !== 'all' ? { odds: oddsFilter } : {}),
    ...(debouncedSearch ? { search: debouncedSearch } : {}),
    ...(scheduleFilter ? { recipe: scheduleFilter } : {}),
    ...(templateFilter ? { template: templateFilter } : {}),
    sort: sort.key,
    direction: sort.direction,
    limit: LIBRARY_PAGE_SIZE,
    offset: page * LIBRARY_PAGE_SIZE,
  }
  const libraryQuery = useQuery({
    queryKey: ['prepacks', 'library', slug, 'page', params],
    queryFn: ({ signal }) => prepacksApi.listPrepacks(slug, params, { signal }),
    enabled: Boolean(slug),
    // Keep the previous page on screen while the next one loads, but never
    // another organization's library.
    placeholderData: (previous, previousQuery) => (previousQuery?.queryKey?.[2] === slug ? previous : undefined),
    // A banner picture is generated about a minute after a scheduled pack is created (#606): look again until it lands.
    refetchInterval: (query) => (query.state.data?.items?.some((pack) => pack.banner_status === 'painting') ? 15000 : false),
  })
  const facetsQuery = useQuery({
    queryKey: ['prepacks', 'library', slug, 'facets'],
    queryFn: ({ signal }) => prepacksApi.getLibraryFacets(slug, { signal }),
    enabled: Boolean(slug),
  })
  const configQuery = useQuery({
    queryKey: ['prepacks', 'configuration', slug],
    queryFn: () => prepacksApi.getConfiguration(slug),
    enabled: Boolean(slug),
  })

  const visiblePacks = useMemo(() => (libraryQuery.data?.items || []).map(normalizePrepack), [libraryQuery.data?.items])
  const invalidateLibrary = () => queryClient.invalidateQueries({ queryKey: ['prepacks', 'library', slug] })
  // The open pack, the selection, the publish dialog, and the lifecycle and delete flows.
  const list = usePackList({ slug, packs: visiblePacks, listQuery: libraryQuery, canConfigure, canPublish, invalidate: invalidateLibrary })
  const { openPack: selectedPack, canDelete } = list
  // The drawer's Edit menu names the schedule that generated the open pack;
  // the schedules list is read once such a pack is opened.
  const drawerRecipeId = canConfigure ? packRecipeId(selectedPack) : null
  const namedRecipeIds = [drawerRecipeId, scheduleFilter].filter(Boolean)
  const scheduleSourceOf = useScheduleSources(slug, { enabled: namedRecipeIds.length > 0, ids: namedRecipeIds })
  const drawerSource = canConfigure && selectedPack ? scheduleSourceOf(selectedPack) : null
  const libraryTotal = facetsQuery.data?.total
  const leagues = facetsQuery.data?.leagues || []
  const sports = facetsQuery.data?.sports || []
  const types = facetsQuery.data?.types || []
  const hasActiveFilters = statusFilter !== 'all' || sportFilter !== 'all' || leagueFilter !== 'all' || typeFilter !== 'all' || oddsFilter !== 'all' || Boolean(debouncedSearch) || Boolean(scheduleFilter || templateFilter)
  useEffect(() => {
    if (!facetsQuery.data || sportFilter === 'all' || sports.includes(sportFilter)) return
    setSportFilter('all')
    setPage(0)
  }, [facetsQuery.data, sportFilter, sports, setPage])

  // A delete can leave the current page past the end; this steps back to the last page.
  const { total: matchingTotal, pageCount } = useLastPageGuard(libraryQuery, { page, setPage, pageSize: LIBRARY_PAGE_SIZE })

  const activeColumns = ALL_COLUMNS.filter((column) => visibleColumns.includes(column.key))

  const clearFilters = () => {
    setSearch('')
    setSportFilter('all')
    setLeagueFilter('all')
    setTypeFilter('all')
    setOddsFilter('all')
    setStatusFilter('all')
    clearSourceFilter()
  }

  const toggleSort = (key) => {
    if (!key) return
    setSort((current) => nextSort(current, key))
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden bg-[var(--prepacks-bg)] text-[var(--wf-color-text)]" onClick={() => setColumnsOpen(false)}>
      <header className="flex shrink-0 items-center justify-between gap-4 border-b border-[var(--prepacks-border)] px-5 pb-4 pt-6 sm:px-8">
        <div>
          <h1 className="text-xl font-semibold">Library</h1>
          <p className="mt-0.5 text-xs text-[var(--prepacks-muted)]">{libraryTotal ?? matchingTotal} packs generated · every sport, league, and event</p>
        </div>
        <div className="flex items-center gap-2 sm:gap-3">
          <span className="hidden text-xs text-[var(--prepacks-muted)] sm:inline">Showing {matchingTotal} of {libraryTotal ?? matchingTotal}</span>
          <button type="button" onClick={() => { libraryQuery.refetch(); facetsQuery.refetch() }} className="flex h-8 w-8 items-center justify-center rounded text-[var(--prepacks-muted)] transition-colors hover:bg-[var(--prepacks-raised)] hover:text-[var(--wf-color-text)]" title="Refresh library"><RefreshCw className={`h-3.5 w-3.5 ${libraryQuery.isFetching ? 'animate-spin' : ''}`} /></button>
        </div>
      </header>

      <div className="flex shrink-0 flex-wrap items-center gap-2 border-b border-[var(--prepacks-border)] bg-[var(--prepacks-surface)] px-5 py-3 sm:px-8" onClick={(event) => event.stopPropagation()}>
        <label className="flex min-w-[220px] flex-1 items-center gap-2 rounded border border-[var(--prepacks-border)] bg-[var(--prepacks-bg)] px-3 py-1.5 transition-colors focus-within:border-[var(--prepacks-accent-text)]">
          <Search className="h-3.5 w-3.5 shrink-0 text-[var(--prepacks-muted)]" />
          <input value={search} onChange={(event) => setSearch(event.target.value)} aria-label="Search prepacks" placeholder="Search title, league, event, or player…" className="min-w-0 flex-1 bg-transparent text-xs text-[var(--wf-color-text)] outline-none placeholder:text-[var(--prepacks-muted)]" />
        </label>
        <select aria-label="Filter by status" value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)} className="rounded border border-[var(--prepacks-border)] bg-[var(--prepacks-bg)] px-3 py-1.5 text-xs text-[var(--prepacks-text)]">{STATUS_FILTERS.map((status) => <option key={status} value={status}>{statusFilterLabel(status)}</option>)}</select>
        {sports.length > 0 ? <select value={sportFilter} onChange={(event) => setSportFilter(event.target.value)} aria-label="Filter by sport" className="rounded border border-[var(--prepacks-border)] bg-[var(--prepacks-bg)] px-3 py-1.5 text-xs text-[var(--prepacks-text)] outline-none hover:border-[var(--prepacks-muted)]"><option value="all">All sports</option>{sports.map((sport) => <option key={sport} value={sport}>{prepackSportLabel(sport)}</option>)}</select> : null}
        <select value={leagueFilter} onChange={(event) => setLeagueFilter(event.target.value)} aria-label="Filter by league" className="rounded border border-[var(--prepacks-border)] bg-[var(--prepacks-bg)] px-3 py-1.5 text-xs text-[var(--prepacks-text)] outline-none hover:border-[var(--prepacks-muted)]"><option value="all">All leagues</option>{leagues.map((league) => <option key={league} value={league}>{league}</option>)}</select>
        <select value={typeFilter} onChange={(event) => setTypeFilter(event.target.value)} aria-label="Filter by type" className="rounded border border-[var(--prepacks-border)] bg-[var(--prepacks-bg)] px-3 py-1.5 text-xs text-[var(--prepacks-text)] outline-none hover:border-[var(--prepacks-muted)]"><option value="all">All types</option>{types.map((type) => <option key={type} value={type}>{packTypeLabel(type)}</option>)}</select>
        <select value={oddsFilter} onChange={(event) => setOddsFilter(event.target.value)} aria-label="Filter by odds" className="rounded border border-[var(--prepacks-border)] bg-[var(--prepacks-bg)] px-3 py-1.5 text-xs text-[var(--prepacks-text)] outline-none hover:border-[var(--prepacks-muted)]">{ODDS_FILTERS.map((filter) => <option key={filter.key} value={filter.key}>{filter.label}</option>)}</select>
        {scheduleFilter || templateFilter ? (
          <button type="button" onClick={clearSourceFilter} aria-label="Show every pack" className="flex items-center gap-1.5 rounded border border-[var(--prepacks-accent-text)] px-2.5 py-1.5 text-xs text-[var(--prepacks-text)]">
            {scheduleFilter
              ? `Schedule: ${scheduleSourceOf({ recipe_id: scheduleFilter })?.name || 'one schedule'}`
              : `Template: ${resolveTemplates(configQuery.data).find((template) => template.id === templateFilter)?.name || 'one template'}`}
            <X className="h-3 w-3 text-[var(--prepacks-muted)]" />
          </button>
        ) : null}

        <div className="relative ml-auto">
          <button type="button" onClick={() => setColumnsOpen((open) => !open)} className="flex items-center gap-1.5 rounded border border-[var(--prepacks-border)] bg-[var(--prepacks-bg)] px-2.5 py-1.5 text-xs text-[var(--prepacks-text)] transition-colors hover:border-[var(--prepacks-muted)]"><SlidersHorizontal className="h-3 w-3 text-[var(--prepacks-muted)]" />Columns<ChevronDown className="h-3 w-3 text-[var(--prepacks-muted)]" /></button>
          {columnsOpen ? (
            <div className="absolute right-0 top-full z-20 mt-1 min-w-[160px] rounded border border-[var(--prepacks-border)] bg-[var(--prepacks-surface)] py-1 shadow-xl">
              {ALL_COLUMNS.map((column) => (
                <button key={column.key} type="button" onClick={() => setVisibleColumns((current) => current.includes(column.key) ? current.filter((key) => key !== column.key) : [...current, column.key])} className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs text-[var(--prepacks-text)] hover:bg-[var(--prepacks-raised)]">
                  <span className={`flex h-3.5 w-3.5 items-center justify-center rounded border ${visibleColumns.includes(column.key) ? 'border-[var(--prepacks-accent)] bg-[var(--prepacks-accent)] text-[var(--wf-color-on-action)]' : 'border-[var(--prepacks-border)]'}`}>{visibleColumns.includes(column.key) ? <Check className="h-2.5 w-2.5 text-[var(--wf-color-on-action)]" /> : null}</span>{column.label}
                </button>
              ))}
            </div>
          ) : null}
        </div>
      </div>

      <section ref={packsRegionRef} tabIndex={-1} aria-label="Packs" className="flex-1 overflow-auto px-5 py-5 focus:outline-none sm:px-8">
        {libraryQuery.isLoading ? (
          <StatePanel state="loading" title="Loading Prepacks" description="Retrieving the organization’s generated packs." />
        ) : libraryQuery.isError ? (
          <StatePanel state="error" title="Library could not be loaded" description={libraryQuery.error?.response?.data?.error || 'Try loading the library again.'} onRetry={() => libraryQuery.refetch()} />
        ) : matchingTotal === 0 && !hasActiveFilters ? (
          <StatePanel state="empty" title="No Prepacks yet" description="Generate a pack to start the organization’s library." onAction={canConfigure ? () => navigate('/dashboard/prepacks/generate') : undefined} actionLabel="Generate prepack" />
        ) : visiblePacks.length === 0 ? (
          <StatePanel state="search-empty" title="No Prepacks match" description="Change or clear the active filters." onClear={clearFilters} />
        ) : (
          <PrepackListTable
            packs={visiblePacks}
            columns={activeColumns}
            sort={sort}
            onSort={toggleSort}
            selected={list.selectedPacks}
            onToggle={list.toggle}
            onToggleAll={list.toggleAll}
            onOpen={list.setOpenPack}
            onDelete={(pack) => list.packDelete.requestDelete([pack.id])}
            canDelete={canDelete}
            preparingDelete={list.packDelete.preparingDelete}
          />
        )}
        {matchingTotal > LIBRARY_PAGE_SIZE ? (
          <div className="mt-4">
            <Pagination page={page + 1} pageCount={pageCount} onPageChange={(next) => setPage(next - 1)} label="Library pages" />
          </div>
        ) : null}
      </section>
      <PackListOverlays
        list={list}
        canConfigure={canConfigure}
        canPublish={canPublish}
        templates={resolveTemplates(configQuery.data)}
        scheduleSource={drawerSource}
        onEdit={(pack) => navigate(`/dashboard/prepacks/generate?id=${pack.id}`)}
        onEditSchedule={drawerSource ? () => navigate(schedulePath(drawerSource.id)) : undefined}
        regionRef={packsRegionRef}
      />
    </div>
  )
}
